// ════════════════════════════════════════════════════════════
//  bannerQueue.js — when several banners happen at once (caP'n, 2026-10-21)
// ════════════════════════════════════════════════════════════
//  "they're really only for important actions but sometimes everything just happens all at once and its hard to see"
//  The banners themselves never change look. What changes is WHEN and WHERE they fly:
//   • LANES — big banners (U'hlti, Transcendence, the Rift) share the middle lane; send-ins have the low lane.
//   • THE WAITING LINE — one banner per side per lane. Another of the same side waits its turn; anything with
//     others waiting behind it plays quicker (QUICK), and nothing waits longer than MAX_WAIT (the log still has it).
//     First come, first served; when several arrive together, most important first: U'hlti, the Rift,
//     Transcendence, then send-ins. Nothing jumps ahead of a banner already waiting (so the Rift can't be starved).
//   • THE CLASH — "when the banners clash, they actually clash and they meet up in the middle with one opponent's
//     banner on their side … it takes up the whole left and right as normal until it clashes with another banner".
//     Your banner and theirs in the same lane at the same time each take their own half and meet in the middle
//     (the crack is drawn by App.css on the meeting edge). When one leaves, the other gets the whole lane back.
//   • The Rift belongs to nobody: it takes the whole lane and both sides wait for it.
//  Pure functions (no React), so tools can test them in Node.
// ════════════════════════════════════════════════════════════

export const BANNER_QUICK = 0.55;      // ⟵ DIAL  a banner with others waiting behind it plays at this share of its time
export const BANNER_MAX_WAIT = 4;      // ⟵ DIAL  seconds a banner may wait its turn before it's let go (the log still has it)
export const BANNER_RANK = { ult: 3, rift: 2, ascend: 1, enter: 0 };   // ⟵ DIAL  who goes first when several arrive together (bigger first)

export const laneOf = (kind) => (kind === "enter" ? "low" : "high");
export const ownerOf = (b) => (b.kind === "rift" ? "both" : b.mine ? "L" : "R");

// does `a` have to wait while `b` is on screen? (same lane, and the same side or the Rift)
const blocks = (a, b) => a.lane === b.lane && (a.owner === b.owner || a.owner === "both" || b.owner === "both");

/**
 * One step of the banners.
 *  shown   — banners on screen now   { key, kind, mine, life, until, secs, half, hold, lane, owner, … }
 *  waiting — banners waiting their turn
 *  add     — new banners this step   { key, kind, mine, life, hold?, … }
 *  now     — ms clock
 * Returns { shown, waiting, changed }.
 */
export function stepBanners(shown, waiting, add, now) {
  let changed = false;
  let on = shown.filter((x) => x.until > now);
  if (on.length !== shown.length) changed = true;
  let line = waiting.concat(add.map((a) => ({ ...a, lane: laneOf(a.kind), owner: ownerOf(a), at: now })));
  const before = line.length;
  line = line.filter((x) => x.hold || now - x.at <= BANNER_MAX_WAIT * 1000);
  if (line.length !== before || add.length) changed = true;
  // first come, first served; among banners that arrived together, the most important goes first
  line.sort((p, q) => p.at - q.at || (BANNER_RANK[q.kind] || 0) - (BANNER_RANK[p.kind] || 0));

  const left = [], started = new Set();
  for (const x of line) {
    // blocked by what's on screen, OR by a more important banner still waiting (so nothing jumps ahead of a waiting Rift)
    if (on.some((y) => blocks(x, y)) || left.some((w) => blocks(x, w))) { left.push(x); continue; }
    started.add(x.key);
    const behind = line.some((y) => y !== x && !started.has(y.key) && blocks(y, x));
    const secs = x.hold ? x.life : x.life * (behind ? BANNER_QUICK : 1);
    on = on.concat({ ...x, secs, until: now + secs * 1000 });
    changed = true;
  }

  // the clash: your banner and theirs in the same lane each take their own half
  on = on.map((x) => {
    const half = x.owner !== "both" && on.some((y) => y !== x && y.lane === x.lane && y.owner !== "both" && y.owner !== x.owner);
    if (half === !!x.half) return x;
    changed = true;
    return { ...x, half };
  });
  return { shown: on, waiting: left, changed };
}
